import { glMatrix, mat4, vec3, vec4 } from 'gl-matrix';
import { Edge, GroupNode, InstanceType, LeafNode, Model, NodeType } from './types';

const NUM_CAPS = 48;
const WHITE: vec4 = vec4.fromValues(1, 1, 1, 1);
const BLACK: vec4 = vec4.fromValues(0, 0, 0, 1);
const MAGENTA: vec4 = vec4.fromValues(1, 0, 1, 1);
const STEEL: vec4 = vec4.fromValues(80/255, 80/255, 80/255, 1);
const DARK_STEEL: vec4 = vec4.fromValues(30/255, 30/255, 30/255, 1);
const RED: vec4 = vec4.fromValues(1, 0, 0, 1);

const origin: LeafNode = { name: "TESTORIGIN", instance: InstanceType.SPHERE, color: MAGENTA };

const generateRotorTop = (): GroupNode => {
  const fanFL: LeafNode = { name: "Fan Front-Left", instance: InstanceType.FAN, color: STEEL }; // FAN
  const fanTransformFL: mat4 = mat4.create();
  mat4.rotateY(fanTransformFL, fanTransformFL, glMatrix.toRadian(45));
  mat4.translate(fanTransformFL, fanTransformFL, vec3.fromValues(25, 25, 0));
  mat4.rotateX(fanTransformFL, fanTransformFL, glMatrix.toRadian(90));
  mat4.scale(fanTransformFL, fanTransformFL, vec3.fromValues(35, 75, 5));
  const fanFR: LeafNode = { name: "Fan Front-Right", instance: InstanceType.FAN, color: STEEL }; // FAN
  const fanTransformFR: mat4 = mat4.create();
  mat4.rotateY(fanTransformFR, fanTransformFR, glMatrix.toRadian(135));
  mat4.translate(fanTransformFR, fanTransformFR, vec3.fromValues(25, 25, 0));
  mat4.rotateX(fanTransformFR, fanTransformFR, glMatrix.toRadian(90));
  mat4.scale(fanTransformFR, fanTransformFR, vec3.fromValues(35, 75, 5));
  const fanBR: LeafNode = { name: "Fan Back-Right", instance: InstanceType.FAN, color: STEEL }; // FAN
  const fanTransformBR: mat4 = mat4.create();
  mat4.rotateY(fanTransformBR, fanTransformBR, glMatrix.toRadian(225));
  mat4.translate(fanTransformBR, fanTransformBR, vec3.fromValues(25, 25, 0));
  mat4.rotateX(fanTransformBR, fanTransformBR, glMatrix.toRadian(90));
  mat4.scale(fanTransformBR, fanTransformBR, vec3.fromValues(35, 75, 5));
  const fanBL: LeafNode = { name: "Fan Back-Left", instance: InstanceType.FAN, color: STEEL }; // FAN
  const fanTransformBL: mat4 = mat4.create();
  mat4.rotateY(fanTransformBL, fanTransformBL, glMatrix.toRadian(315));
  mat4.translate(fanTransformBL, fanTransformBL, vec3.fromValues(25, 25, 0));
  mat4.rotateX(fanTransformBL, fanTransformBL, glMatrix.toRadian(90));
  mat4.scale(fanTransformBL, fanTransformBL, vec3.fromValues(35, 75, 5));
  const center: LeafNode = { name: "Center", instance: InstanceType.SEMI_SPHERE, color: BLACK}; // SEMI
  const centerTransform = mat4.create();
  mat4.translate(centerTransform, centerTransform, vec3.fromValues(0, 25, 0));
  mat4.rotateX(centerTransform, centerTransform, glMatrix.toRadian(270));
  mat4.scale(centerTransform, centerTransform, vec3.fromValues(50, 50, 50));
  const centerLight: LeafNode = { name: "Center Light", instance: InstanceType.SEMI_SPHERE, color: RED}; // SEMI
  const centerLightTransform = mat4.create();
  mat4.translate(centerLightTransform, centerLightTransform, vec3.fromValues(0, 48, 0));
  mat4.rotateX(centerLightTransform, centerLightTransform, glMatrix.toRadian(270));
  mat4.scale(centerLightTransform, centerLightTransform, vec3.fromValues(15, 15, 15));
  return {name: "Rotor Top", edges: [{
      to: center, toType: NodeType.LEAF_NODE, transformation: centerTransform,
    }, {
      to: centerLight, toType: NodeType.LEAF_NODE, transformation: centerLightTransform,
    }, {
      to: fanFR, toType: NodeType.LEAF_NODE, transformation: fanTransformFR,
    }, {
      to: fanFL, toType: NodeType.LEAF_NODE, transformation: fanTransformFL,
    }, {
      to: fanBL, toType: NodeType.LEAF_NODE, transformation: fanTransformBL,
    }, {
      to: fanBR, toType: NodeType.LEAF_NODE, transformation: fanTransformBR,
    }]};
};

const generateRotor = (): GroupNode => {
  const base = { name: "Rotor Base", instance: InstanceType.CYLINDER, color: BLACK }; // CYL
  const baseTransform = mat4.create();
  mat4.scale(baseTransform, baseTransform, vec3.fromValues(50, 50, 50));
  mat4.rotateX(baseTransform, baseTransform, glMatrix.toRadian(90))
  const rotorTop = generateRotorTop();
  return {name: "Rotor", edges: [{
      to: base, toType: NodeType.LEAF_NODE, transformation: baseTransform,
    }, {
      to: rotorTop, toType: NodeType.GROUP_NODE
  }]};
};

const generateArm = (): GroupNode => {
  const armConnection = { name: "Arm Connector", instance: InstanceType.CYLINDER, color: DARK_STEEL }; // CYL
  const armConnectionTransform: mat4 =  mat4.create();
  mat4.rotateY(armConnectionTransform, armConnectionTransform, glMatrix.toRadian(90));
  mat4.translate(armConnectionTransform, armConnectionTransform, vec3.fromValues(0, 0, -100));
  mat4.scale(armConnectionTransform, armConnectionTransform, vec3.fromValues(25, 25, 200));
  const rotor = generateRotor();
  return {name: "Arm", edges: [{
      to: armConnection, toType: NodeType.LEAF_NODE, transformation: armConnectionTransform,
    }, {
      to: rotor, toType: NodeType.GROUP_NODE
    // }, {
    //   to: origin, toType: NodeType.LEAF_NODE, transformation: mat4.scale(mat4.create(), mat4.create(), vec3.fromValues(50, 50, 50))
    }]};
};

const generateCap = (): GroupNode => {

  const edges: Edge[] = [];
  for (let i = 0; i < 6; i++) {
    const cap: LeafNode = { name: "Cap", instance: InstanceType.SEMI_SPHERE, color: (i % 2 === 0) ? STEEL : BLACK }; // SEMI
    const capTransform = mat4.create();
    let size = 200;
    const capSize = size * (1 - (i / NUM_CAPS));
    mat4.translate(capTransform, capTransform, vec3.fromValues(0, size - capSize, 0));
    mat4.rotateX(capTransform, capTransform, glMatrix.toRadian(-90));
    mat4.scale(capTransform, capTransform, vec3.fromValues(capSize, capSize, capSize));
    edges.push({
      to: cap,
      toType: NodeType.LEAF_NODE,
      transformation: capTransform,
    });
  }

  return {
    name: "Cap",
    edges,
  };
};

const generateBody = (): GroupNode => {
  const base = { name: "Body Base", instance: InstanceType.CYLINDER, color: BLACK }; // CYL
  const baseTransform = mat4.create();
  mat4.scale(baseTransform, baseTransform, vec3.fromValues(200, 100, 200));
  mat4.rotateX(baseTransform, baseTransform, glMatrix.toRadian(90))
  const eyeR = {}; // CYL
  const eyeL = {}; // CYL
  const nose = {}; // ? cone? ?? ??!!!
  return {
    name: "Body",
    edges: [
      { to: base, toType: NodeType.LEAF_NODE, transformation: baseTransform },
      { to: generateCap(), toType: NodeType.GROUP_NODE }

    ]
  };
};

export const generateQuadcopter = (): GroupNode => {
  const armTransformBR: mat4 = mat4.create();
  mat4.rotateY(armTransformBR, armTransformBR, glMatrix.toRadian(45));
  mat4.translate(armTransformBR, armTransformBR, vec3.fromValues(300, 0, 0));
  const armTransformBL: mat4 = mat4.create();
  mat4.rotateY(armTransformBL, armTransformBL, glMatrix.toRadian(135));
  mat4.translate(armTransformBL, armTransformBL, vec3.fromValues(300, 0, 0));
  const armTransformFL: mat4 = mat4.create();
  mat4.rotateY(armTransformFL, armTransformFL, glMatrix.toRadian(225));
  mat4.translate(armTransformFL, armTransformFL, vec3.fromValues(300, 0, 0));
  const armTransformFR: mat4 = mat4.create();
  mat4.rotateY(armTransformFR, armTransformFR, glMatrix.toRadian(315));
  mat4.translate(armTransformFR, armTransformFR, vec3.fromValues(300, 0, 0));
  return {name: "Quadcopter", edges: [{
      to: generateBody(), toType: NodeType.GROUP_NODE
    }, {
      to: generateArm(), toType: NodeType.GROUP_NODE, transformation: armTransformFL
    }, {
      to: generateArm(), toType: NodeType.GROUP_NODE, transformation: armTransformFR
    }, {
      to: generateArm(), toType: NodeType.GROUP_NODE, transformation: armTransformBR
    }, {
      to: generateArm(), toType: NodeType.GROUP_NODE, transformation: armTransformBL
    }]
  };
};

export const generateQuadcopterModel = (): Model => {
  return {name: 'Quadcopter', scene: generateQuadcopter(), sceneNodeType: NodeType.GROUP_NODE};
}